import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@ixicare/ui';
import { X } from 'lucide-react';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import styles from './ImpersonationBanner.module.css';

const formatRemaining = (seconds: number): string => {
  const safe = Math.max(0, seconds);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const ImpersonationBanner: React.FC = observer(() => {
  const { auth } = useStores();
  const navigate = useNavigate();
  const [now, setNow] = React.useState<number>(() => Date.now());

  React.useEffect(() => {
    if (!auth.isImpersonating) return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [auth.isImpersonating]);

  const remainingSeconds = React.useMemo(() => {
    if (!auth.impersonationExpiresAt) return 0;
    return Math.floor((auth.impersonationExpiresAt.getTime() - now) / 1000);
  }, [auth.impersonationExpiresAt, now]);

  React.useEffect(() => {
    if (auth.isImpersonating && remainingSeconds <= 0 && auth.impersonationExpiresAt) {
      auth.exitLoginAs().then(() => navigate('/healthcare-providers'));
    }
  }, [auth, remainingSeconds, navigate]);

  if (!auth.isImpersonating || !auth.user) return null;

  const handleExit = async () => {
    await auth.exitLoginAs();
    navigate('/healthcare-providers');
  };

  return (
    <div className={styles.Banner}>
      <span className={styles.Text}>
        {t('impersonation.banner.title', { name: `${auth.user.firstName} ${auth.user.lastName}` })}
      </span>
      <span className={styles.Countdown}>
        {t('impersonation.banner.expiresIn', { time: formatRemaining(remainingSeconds) })}
      </span>
      <span className={styles.ExitWrapper}>
        <IconButton lucide={X} onClick={handleExit} />
      </span>
    </div>
  );
});

export default ImpersonationBanner;
